import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { ShieldWarning, LockKey, Timer } from '@phosphor-icons/react';

export const openElevationDialog = (agencyId, agencyName) => {
  window.dispatchEvent(new CustomEvent('open-elevation-dialog', { detail: { agencyId, agencyName } }));
};

const formatRemaining = (expiresAt) => {
  const ms = new Date(expiresAt) - new Date();
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60000);
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
};

export const ElevationBanner = ({ isAzaiStaff }) => {
  const [elevation, setElevation] = useState(null);
  const [, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/platform-access/status');
      setElevation(r.data.active ? r.data.elevation : null);
    } catch {
      setElevation(null);
    }
  }, []);

  useEffect(() => {
    if (!isAzaiStaff) return;
    refresh();
    const poll = setInterval(refresh, 60000);
    const tick = setInterval(() => setTick((t) => t + 1), 30000);
    window.addEventListener('elevation-changed', refresh);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      window.removeEventListener('elevation-changed', refresh);
    };
  }, [isAzaiStaff, refresh]);

  if (!isAzaiStaff || !elevation) return null;

  const revoke = async () => {
    try {
      await api.post(`/platform-access/${elevation.id}/revoke`);
      toast.success('Elevation revoked');
      window.dispatchEvent(new Event('elevation-changed'));
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  return (
    <div
      className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2 bg-amber-500 text-amber-950 text-sm font-medium"
      data-testid="elevation-banner"
    >
      <ShieldWarning size={18} weight="fill" />
      <span>
        Platform elevation active: <strong>{elevation.target_agency_name || elevation.target_agency_id}</strong>
      </span>
      <Badge variant="outline" className="border-amber-800 text-amber-900 uppercase text-[10px]">
        {elevation.scope}
      </Badge>
      <span className="flex items-center gap-1 text-amber-900">
        <Timer size={14} /> expires in {formatRemaining(elevation.expires_at)}
      </span>
      <span className="hidden md:inline text-amber-900 truncate max-w-xs" title={elevation.reason}>
        — {elevation.reason}
      </span>
      <div className="flex-1" />
      <Button size="sm" variant="outline" className="h-7 border-amber-800 text-amber-950 hover:bg-amber-400"
        onClick={revoke} data-testid="elevation-revoke-btn">
        Revoke
      </Button>
    </div>
  );
};

export const ElevationDialog = () => {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState({ agencyId: '', agencyName: '' });
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState('read');
  const [duration, setDuration] = useState('30');
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setTarget({ agencyId: e.detail.agencyId, agencyName: e.detail.agencyName });
      setReason('');
      setOtp('');
      setOpen(true);
    };
    window.addEventListener('open-elevation-dialog', handler);
    return () => window.removeEventListener('open-elevation-dialog', handler);
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/platform-access/elevate', {
        target_agency_id: target.agencyId,
        reason,
        scope,
        duration_minutes: parseInt(duration, 10),
        otp,
      });
      toast.success(`Elevated to ${target.agencyName} (${scope})`);
      localStorage.setItem('currentAgencyId', target.agencyId);
      window.dispatchEvent(new Event('elevation-changed'));
      setOpen(false);
      window.location.reload();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const structured = e.response?.data?.detail_object;
      if (structured?.error === 'mfa_enrolment_required' || detail?.error === 'mfa_enrolment_required' || detail?.includes?.('MFA')) {
        toast.error('MFA enrolment required — set it up under Account → MFA first.');
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Elevation failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="elevation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LockKey size={20} className="text-amber-600" />
            Request tenant elevation
          </DialogTitle>
          <DialogDescription>
            Time-limited, audited access to <strong>{target.agencyName}</strong>. MFA required.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea
            placeholder="Reason for access (min 10 characters, recorded in the audit trail)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="elevation-reason-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="elevation-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="read">Read-only</SelectItem>
                <SelectItem value="write">Read + write</SelectItem>
              </SelectContent>
            </Select>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="elevation-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
                <SelectItem value="480">8 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input
            placeholder="MFA code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            maxLength={16}
            data-testid="elevation-otp-input"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700"
            disabled={submitting || reason.trim().length < 10 || otp.length < 6}
            onClick={submit}
            data-testid="elevation-submit-btn"
          >
            {submitting ? 'Requesting…' : 'Elevate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
